import { useRef, useEffect, useState } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF, Environment } from '@react-three/drei'
import * as THREE from 'three'

// Breakpoint per mobile (standard Tailwind md)
const MOBILE_BREAKPOINT = 768

interface ContactPlantSceneProps {
  mouseX: number
  mouseY: number
}

function ContactPlant({ mouseX, mouseY }: ContactPlantSceneProps) {
  const { scene } = useGLTF('/3D/plant.glb')
  const meshRef = useRef<THREE.Group>(null)
  const [isMobile, setIsMobile] = useState(false)

  // Rileva se siamo su mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  useEffect(() => {
    // Clone the scene to modify materials
    const clonedScene = scene.clone()
    
    // Make all materials green with matte finish
    clonedScene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        (child as THREE.Mesh).material = new THREE.MeshStandardMaterial({
          color: '#7ec876',
          metalness: 0.0,
          roughness: 1.0,
          envMapIntensity: 0.1
        })
      }
    })
    
    if (meshRef.current) {
      meshRef.current.clear()
      meshRef.current.add(clonedScene)
    }
  }, [scene])

  useFrame((state, delta) => {
    if (meshRef.current) {
      if (isMobile) {
        // Su mobile: rotazione autonoma lenta
        meshRef.current.rotation.y += delta * 0.1
        meshRef.current.rotation.x = Math.sin(state.clock.getElapsedTime() * 0.3) * 0.1
      } else {
        // Su desktop: usa il mouse
        const x = (mouseX / window.innerWidth) * 2 - 1
        const y = -(mouseY / window.innerHeight) * 2 + 1
        meshRef.current.rotation.y = x * 0.5
        meshRef.current.rotation.x = y * 0.3
      }
      
      // Floating animation
      meshRef.current.position.y = Math.sin(state.clock.getElapsedTime() * 0.5) * 0.1
      
      // Scale: piu piccolo su mobile (80%), normale su desktop (120%)
      const scale = isMobile ? 0.8 : 1.2
      meshRef.current.scale.set(scale, scale, scale)
    }
  })

  return <group ref={meshRef} />
}

export function ContactPlantScene({ mouseX, mouseY }: ContactPlantSceneProps) {
  return (
    <div className="w-full h-64 md:h-[500px] lg:h-[600px]">
      <Canvas 
        id="contact-plant-canvas"
        camera={{ position: [0, 0, 5], fov: 50 }}
        style={{ touchAction: 'none' }}
        gl={{ powerPreference: 'low-power' }}
      >
        <ambientLight intensity={0.5} />
        <pointLight position={[2, 2, 2]} intensity={1} color="#ffffff" />
        <pointLight position={[-2, -2, -2]} intensity={0.5} color="#ffffff" />
        <ContactPlant mouseX={mouseX} mouseY={mouseY} />
        <Environment preset="studio" />
      </Canvas>
    </div>
  )
}
